// Offline Support (120): React hook exposing the offline write-queue state for
// one match, so score pages can show pending/rejected writes and the online
// status without each page touching IndexedDB directly.

import { useCallback, useEffect, useState } from "react";
import type { OfflineScoreQueueItem } from "@dugads/shared";
import {
  listQueueForMatch,
  subscribeQueue,
  clearRejectedForMatch,
  removeQueueItem,
} from "./write-queue";
import { useOnlineStatus } from "./connectivity";

export interface ScoreQueueState {
  /** Browser connectivity (a hint, not a guarantee the API is reachable). */
  online: boolean;
  /** Writes still awaiting the server (status pending or syncing), oldest first. */
  pending: OfflineScoreQueueItem[];
  /** Writes the server refused on replay; surfaced for re-entry. */
  rejected: OfflineScoreQueueItem[];
  /** Count of pending + syncing items. */
  pendingCount: number;
  /** Drop all rejected items for this match (coach acknowledged the notice). */
  dismissRejected: () => Promise<void>;
  /** Drop a single queued item by its client event id. */
  dismissItem: (clientEventId: string) => Promise<void>;
}

export function useScoreQueue(matchId: string | undefined): ScoreQueueState {
  const online = useOnlineStatus();
  const [items, setItems] = useState<OfflineScoreQueueItem[]>([]);

  const refresh = useCallback(() => {
    if (!matchId) {
      setItems([]);
      return;
    }
    listQueueForMatch(matchId)
      .then(setItems)
      .catch(() => setItems([]));
  }, [matchId]);

  useEffect(() => {
    refresh();
    return subscribeQueue(refresh);
  }, [refresh]);

  const dismissRejected = useCallback(async () => {
    if (matchId) await clearRejectedForMatch(matchId);
  }, [matchId]);

  const dismissItem = useCallback(async (clientEventId: string) => {
    await removeQueueItem(clientEventId);
  }, []);

  const pending = items.filter(
    (i) => i.status === "pending" || i.status === "syncing",
  );
  const rejected = items.filter((i) => i.status === "rejected");

  return {
    online,
    pending,
    rejected,
    pendingCount: pending.length,
    dismissRejected,
    dismissItem,
  };
}
